import styled from "styled-components";

export const Header = styled.header`
  margin: 40px 0;
`;

export const Heading = styled.div`
  font-size: 1.5em;
  font-weight: 700;
  margin-bottom: 5px;

  @media screen and (min-width: 768px) {
    font-size: 2em;
  }
`;

export const Subheading = styled.div`
  margin-bottom: 5px;
`;

export const Notice = styled.div`
  font-size: 0.75em;
  margin-bottom: 40px;
`;

export const FieldLayout = styled.div`
  padding: 0 5px;
  flex: ${props => props.weight || 1};
`;

export const FieldBody = styled.div`
  border: 1px solid #ddd;
  border-radius: 3px;
  position: relative;
`;

export const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  text-transform: uppercase;
  position: absolute;
  color: #979797;
  top: 5px;
  left: 10px;
  font-size: 0.8125em;
  transform-origin: top left;
  transition: transform 100ms linear;
  transform: ${props => props.empty ? "scale(1.0)" : "scale(0.8)"};
  cursor: text;
  user-select: none;

  input:focus + & {
    transform: scale(0.8);
  }
`;

export const Input = styled.input`
  width: 100%;
  border: none;
  padding: 20px 10px 5px;
  background: none;
  font-size: 1em;

  ${props => props.$invalid && `
    background-color: #f9dcdb;
    border: 1px solid #db3a34;
    border-radius: 2px;
  `}
`;

export const Errors = styled.div`
  position: absolute;
  right: 0;
  bottom: 100%;
  background-color: #db3a34;
  color: #fff;
  padding: 3px 6px;
  font-size: 0.875em;
  border-radius: 3px;
  opacity: 0;
  transform: translate(-6px, 12px);
  transition: opacity 100ms linear, transform 100ms linear;
  white-space: pre-line;
  min-width: 120px;

  *:focus ~ &, *:focus > & {
    opacity: 1;
    transform: translate(-6px, 6px);
  }

  ${props => props.$persistErrors && `
    opacity: 1;
    transform: translate(-6px, 6px);
  `}

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    right: 0;
    border: 7px solid transparent;
    border-top-color: #db3a34;
    border-bottom: 0;
    transform: translate(-5px, 100%);
  }
`;

export const Form = styled.form`
  width: 100%;
  margin: auto -5px 20px;
  ${FieldLayout} {
    margin-bottom: 10px;
  }
`;

export const FieldGroup = styled.div`
  display: flex;
`;

export const SubstepHeading = styled.h2`
  font-size: 1em;
  margin-bottom: 10px;
`;

export const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const ActionButton = styled.input.attrs({
  type: "submit",
})`
  appearance: none;
  background-color: #00C19D;
  border: none;
  border-radius: 2px;
  color: #fff;
  display: block;
  font-size: 1em;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
  padding: 10px 40px;
  margin: 0 5px 0 auto;

  &:hover {
    background-color: #00755f;
  }

  &:disabled {
    background-color: #cccccc;
  }

  @media screen and (max-width: 768px) {
    width: 100%;
    margin: 0 5px;
  }
`;

export const Title = styled.h2`
  border-bottom: 1px solid #333;
  font-weight: 700;
  padding-bottom: 20px;
  margin: 40px auto;

  @media screen and (max-width: 1024px) {
    margin: 30px auto;
    font-size: 1.5em;
  }

  @media screen and (max-width: 768px) {
    margin: 20px auto;
    font-size: 1.25em;
  }
`;

export const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

export const PageSizeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const PageSizeLabel = styled.label`
  color: #666666;
  font-size: 0.875em;
`;

export const PageSizeSelect = styled.select`
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 6px 8px;
  background-color: #ffffff;
  margin-left: 8px;
`;

export const PageIndicator = styled.div`
  color: #666666;
  font-size: 0.875em;

  @media screen and (max-width: 768px) {
    text-align: center;
  }
`;

export const PageButtons = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;

  @media screen and (max-width: 768px) {
    margin-left: 0;
  }
`;

export const PageButton = styled.button`
  appearance: none;
  border: 1px solid #ddd;
  border-radius: 3px;
  background-color: #ffffff;
  padding: 6px 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    color: #aaaaaa;
    cursor: not-allowed;
  }
`;

export const ItemLayout = styled.div`
  display: flex;
  margin-bottom: 12px;
  border: 1px solid #ddd;
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  padding: 10px;
`;

export const EntrantLayout = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
`;

export const EntryDataLayout = styled.div`
  text-align: right;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-end;
`;

export const Name = styled.div`
  font-weight: bold;
`;

export const Email = styled.div`
  font-size: 0.875em;
  color: #aaaaaa;
  display: block;
`;

export const TicketRange = styled.div`
  color: #000000;
  font-size: 0.875em;
  ${props => ["DISPUTED", "REFUNDED"].includes(props.status) && `
    text-decoration: line-through;
  `}
`;

export const Date = styled.div`
  color: #aaaaaa;
  font-size: 0.875em;
`;

const amountBackgroundColor = ({ status }) => {
  if (["DISPUTED", "REFUNDED", "PROCESSING"].includes(status)) {
    return "#aaaaaa";
  }

  if (["SUCCESSFUL", "FREE", "MANUAL", "COMPLIMENTARY"].includes(status)) {
    return "#77aa16";
  }

  return "#aaaaaa";
};

export const Amount = styled.div`
  position: relative;
  display: inline-block;
  padding: 6px 8px;
  background-color: ${amountBackgroundColor};
  color: #ffffff;
  text-transform: uppercase;
  font-weight: bold;
  min-width: 96px;
  text-align: center;
  border-radius: 3px;
  font-size: 0.875em;

  ${props => ["DISPUTED", "REFUNDED"].includes(props.status) && `
    background-color: #cccccc;

    &:after {
      content: "${props.status}";
      font-weight: bold;
      font-size: 0.5em;
      background-color: #db3a34;
      color: #ffffff;
      padding: 6px 12px;
      border-radius: 6px;
      position: absolute;
      top: 0;
      right: 50px;
      transform: translateY(-50%) rotate(-15deg);
    }
  `}
`;

export const Captcha = styled.div`
  margin-bottom: 10px;
`;
